import { Component, Input, OnInit, OnDestroy, SimpleChanges, OnChanges, ChangeDetectorRef} from '@angular/core'
import { TranslocoModule } from '@jsverse/transloco'
import { QuestionAndAnswerComponent } from '../question-and-answer.component'
import { CommonModule } from '@angular/common'
import { concatMap, delay, from, identity, Observable, of, scan, tap, concat} from 'rxjs'
import { WordOrAnnotations } from '../../../../types'
import { ConfigService } from '../../../../services/config.service'


@Component({
   selector: 'app-answer',
   standalone: true,
   imports: [CommonModule, TranslocoModule],
   templateUrl: './answer.component.html',
   styles: ''
})
export class AnswerComponent implements OnInit, OnDestroy, OnChanges {

   @Input() answer!: string
   @Input() parent!: QuestionAndAnswerComponent
   @Input() language = 'en'

   stringAnswer!: Observable<string>

   answer$!: Observable<WordOrAnnotations[][]>

   constructor(private cdr: ChangeDetectorRef, private config: ConfigService) {}

   ngOnInit() {
      this.parent.scrolltoRelevantElement()
      this.buildAnswer()
   }

   isString(s: WordOrAnnotations) {
      return typeof s === 'string'
   }

   getAnnotations(s: WordOrAnnotations) {
      return s as number[]
   }

   buildAnswer() {
      let answerFromParent$
      // if we're getting a stream of words, we can add a delay between each word
      let addDelay = this.config.currentConfig.streamAnswer

      if(this.parent.qanda.answer?.answer$) {
         answerFromParent$ = this.parent.qanda.answer?.answer$
      }
      // the answer$ is undefined, so  we're not getting a stream of words, but a single string, so we should not do the delay thing
      else {
         answerFromParent$ = of(this.parent.qanda.answer?.answer ?? '')
         addDelay = false
      }

      if (answerFromParent$) {
         this.answer$ = answerFromParent$.pipe(
            concatMap(data => {
               // split on whitespace and line endings, but keep the space/line ending (using the capturing group)
               const words = data.split(/(\s|\n)/)
               // from this array create an observable that emits each word
               return from(words).pipe(
                  concatMap(word => {
                     // if the word contains annotations, get them as an array of numbers, the component will know how to display them
                     const annotations = this.extractAnnotations(word)
                     if (annotations.length > 0) {
                        return concat(
                           of<WordOrAnnotations>(word.split('[')[0]).pipe(addDelay ? delay(10) : identity),
                           of<WordOrAnnotations>(word.slice(word.lastIndexOf(']') + 1) || ''),
                           of<WordOrAnnotations>(annotations)
                        )
                     }
                     // just add the word as is
                     return of<WordOrAnnotations>(word).pipe(addDelay ? delay(10) : identity)
                  })
               )
            }),
            // collect the words in an array (with a 'scan' reducer) so we can display all the words that are emitted
            // otherwise the UI would show each individual word very quickly and then just only the last one :)
            scan((acc, curr:WordOrAnnotations) => {
               if (acc.length === 0) {
                  acc.push([])
               }
               const paragraph = acc[acc.length - 1]

               if (typeof curr === 'string') {
                  if (curr === '\n'){
                     // add the line break to the current paragraph and start a new paragraph
                     paragraph.push(curr)
                     acc.push([])
                  } else if (typeof paragraph.at(-1) === 'string') {
                     // append the word to the last word in the paragraph
                     paragraph[paragraph.length - 1] += ' ' + curr
                  } else {
                     paragraph.push(curr)
                  }
               } else {
                  // add annotation
                  paragraph.push(curr)
               }
               return acc
            }, [[]] as WordOrAnnotations[][]))
            // trigger angular changedetection, this seems to be needed to update UI still :(
            .pipe(tap(() => this.cdr.detectChanges()))
      }
   }

   ngOnDestroy() {
      this.parent.qanda.answer?.answer$?.unsubscribe()
   }

   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   ngOnChanges(_changes: SimpleChanges) {
      this.parent.scrolltoRelevantElement()
   }

   extractAnnotations(word: string): number[] {
      let numbersArray: number[] = []
      let match

      // eslint-disable-next-line no-cond-assign
      while (match = this.parent.regex.exec(word)) {
         // Split the matched string by commas and trim any whitespace
         numbersArray = numbersArray.concat(match[1].split(/, ?/g).map((n) => parseInt(n)))
      }
      return numbersArray
   }
}